/**
 * FieldPermissionsPage — `/security/field-permissions`.
 *
 * F-387 / `RoleField` editor surface. Lets an operator pick a role + a model
 * and toggle the (canView, canEdit, canAdd) tuple per field, then persists
 * via `PUT /v1/identity/roles/:roleId/fields` (replace-all per
 * (roleId, model) pair). Hydrate-on-load via `GET …/fields?model=…` so the
 * editor reflects what was previously persisted, not just baked-in defaults.
 *
 * The model + field catalogue is mirrored client-side — once `service-rbac`
 * exposes `GET /v1/identity/rbac/models` it becomes a live query.
 */
import { type JSX, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Pencil, PlusCircle, Save } from 'lucide-react';

import {
  type RoleFieldGrant,
  listRoleFields,
  listRoles,
  replaceRoleFields,
} from '../../lib/api/identity-admin';
import { PageHeader } from '../shared/StaffUI';

interface ModelField {
  readonly name: string;
  readonly type: string;
  readonly sensitivity: 'PII' | 'FINANCIAL' | 'OPERATIONAL';
}

interface ModelDef {
  readonly id: string;
  readonly displayName: string;
  readonly fields: readonly ModelField[];
}

const MODELS: readonly ModelDef[] = [
  {
    id: 'Customer',
    displayName: 'Customer',
    fields: [
      { name: 'fullName',   type: 'string', sensitivity: 'PII' },
      { name: 'phone',      type: 'string', sensitivity: 'PII' },
      { name: 'email',      type: 'string', sensitivity: 'PII' },
      { name: 'nin',        type: 'string', sensitivity: 'PII' },
      { name: 'bvn',        type: 'string', sensitivity: 'PII' },
      { name: 'address',    type: 'json',   sensitivity: 'PII' },
      { name: 'kycTier',    type: 'enum',   sensitivity: 'OPERATIONAL' },
    ],
  },
  {
    id: 'Wallet',
    displayName: 'Wallet',
    fields: [
      { name: 'walletNumber',     type: 'string',  sensitivity: 'OPERATIONAL' },
      { name: 'balanceCached',    type: 'decimal', sensitivity: 'FINANCIAL' },
      { name: 'dailyInflowToday', type: 'decimal', sensitivity: 'FINANCIAL' },
      { name: 'kycTier',          type: 'enum',    sensitivity: 'OPERATIONAL' },
      { name: 'status',           type: 'enum',    sensitivity: 'OPERATIONAL' },
    ],
  },
  {
    id: 'WalletTransaction',
    displayName: 'WalletTransaction',
    fields: [
      { name: 'amount',       type: 'decimal', sensitivity: 'FINANCIAL' },
      { name: 'balanceAfter', type: 'decimal', sensitivity: 'FINANCIAL' },
      { name: 'counterRef',   type: 'string',  sensitivity: 'OPERATIONAL' },
      { name: 'metadata',     type: 'json',    sensitivity: 'OPERATIONAL' },
    ],
  },
  {
    id: 'MailItem',
    displayName: 'MailItem',
    fields: [
      { name: 'trackingNumber', type: 'string',  sensitivity: 'OPERATIONAL' },
      { name: 'sender',         type: 'json',    sensitivity: 'PII' },
      { name: 'receiver',       type: 'json',    sensitivity: 'PII' },
      { name: 'declaredValue',  type: 'decimal', sensitivity: 'FINANCIAL' },
      { name: 'weight',         type: 'decimal', sensitivity: 'OPERATIONAL' },
    ],
  },
];

interface Grant {
  readonly canView: boolean;
  readonly canEdit: boolean;
  readonly canAdd: boolean;
}

const EMPTY_GRANT: Grant = { canView: false, canEdit: false, canAdd: false };

const DEFAULT_MODEL_ID = MODELS[0]?.id ?? 'Customer';

export function FieldPermissionsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [roleId, setRoleId] = useState<string>('');
  const [modelId, setModelId] = useState<string>(DEFAULT_MODEL_ID);
  const [grants, setGrants] = useState<Record<string, Grant>>({});
  const [reason, setReason] = useState('');
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const roles = useQuery({
    queryKey: ['identity', 'roles'],
    queryFn: listRoles,
    retry: false,
  });

  // Default the role selector to the first non-system role so the editor lands
  // on something the operator can actually mutate.
  const editableRoles = useMemo(
    () => (roles.data ?? []).filter((r) => !r.isSystem),
    [roles.data],
  );
  useEffect(() => {
    if (!roleId && editableRoles.length > 0 && editableRoles[0]) {
      setRoleId(editableRoles[0].id);
    }
  }, [editableRoles, roleId]);

  // Hydrate the editor with the persisted grants for the current
  // (roleId, modelId) pair.
  const fields = useQuery({
    queryKey: ['identity', 'role-fields', roleId, modelId],
    queryFn: () => listRoleFields(roleId, modelId),
    enabled: roleId.length > 0,
    retry: false,
  });

  // When the server-side rows arrive, replace local edits with the hydrated
  // state for this (role, model) pair so the operator sees what's persisted.
  useEffect(() => {
    if (!fields.data) return;
    setGrants((prev) => {
      const next = { ...prev };
      // Drop any in-memory state for the current pair so we re-seed cleanly.
      for (const k of Object.keys(prev)) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- intentional: clearing keys derived from a controlled (role, model) tuple
        if (k.startsWith(`${roleId}.${modelId}.`)) delete next[k];
      }
      for (const row of fields.data) {
        next[`${roleId}.${modelId}.${row.field}`] = {
          canView: row.canView,
          canEdit: row.canEdit,
          canAdd: row.canAdd,
        };
      }
      return next;
    });
  }, [fields.data, roleId, modelId]);

  const model = useMemo(() => MODELS.find((m) => m.id === modelId) ?? MODELS[0], [modelId]);
  const grantKey = (field: string): string => `${roleId}.${modelId}.${field}`;

  function getGrant(field: string): Grant {
    return grants[grantKey(field)] ?? EMPTY_GRANT;
  }

  function toggle(field: string, key: keyof Grant): void {
    setGrants((prev) => {
      const k = grantKey(field);
      const current = prev[k] ?? EMPTY_GRANT;
      return { ...prev, [k]: { ...current, [key]: !current[key] } };
    });
  }

  const save = useMutation({
    mutationFn: () => {
      const payload: RoleFieldGrant[] = (model?.fields ?? []).map((f) => {
        const g = getGrant(f.name);
        return { field: f.name, canView: g.canView, canEdit: g.canEdit, canAdd: g.canAdd };
      });
      return replaceRoleFields(roleId, {
        model: modelId,
        reason: reason.trim() || undefined,
        grants: payload,
      });
    },
    onSuccess: () => {
      setSavedAt(new Date().toLocaleTimeString('en-GB'));
      void queryClient.invalidateQueries({
        queryKey: ['identity', 'role-fields', roleId, modelId],
      });
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Field permissions"
        description="Per-role, per-model field access editor. Backs the JWT field_permissions claim."
      />

      <div className="bg-white border border-border rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Role</label>
          <select
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            data-testid="field-perm-role"
            className="px-3 py-2 border border-border rounded-lg text-sm min-w-[200px]"
          >
            <option value="">— Select role —</option>
            {editableRoles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} ({r.id})
              </option>
            ))}
          </select>
          {roles.data && (roles.data.filter((r) => r.isSystem).length > 0) ? (
            <p className="text-[10px] text-muted mt-1">
              System roles ({roles.data.filter((r) => r.isSystem).length}) are immutable
              and hidden from the picker.
            </p>
          ) : null}
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Model</label>
          <select
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
            data-testid="field-perm-model"
            className="px-3 py-2 border border-border rounded-lg text-sm min-w-[200px]"
          >
            {MODELS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1" />
        <div className="flex-1 min-w-[260px]">
          <label className="block text-xs font-medium text-muted mb-1">Change reason</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="field-perm-reason"
            placeholder="Required for audit. Why are these grants changing?"
            className="w-full px-3 py-2 border border-border rounded-lg text-sm"
          />
        </div>
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending || roleId.length === 0}
          data-testid="field-perm-save"
          className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold text-white bg-nipost-green rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {save.isPending ? 'Saving…' : 'Save grants'}
        </button>
      </div>

      {save.isError ? (
        <div
          data-testid="field-perm-error"
          className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800"
        >
          {(save.error as Error).message}
        </div>
      ) : null}
      {savedAt ? (
        <p className="text-sm text-nipost-green" data-testid="field-perm-saved">
          Saved at {savedAt}. Affected user sessions are invalidated on next role grant
          (existing tokens stay valid until their TTL elapses).
        </p>
      ) : null}
      {fields.isError ? (
        <p className="text-xs text-danger">{(fields.error as Error).message}</p>
      ) : null}

      <div className="bg-white border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
            <tr>
              <th className="text-left px-4 py-2">Field</th>
              <th className="text-left px-4 py-2">Type</th>
              <th className="text-left px-4 py-2">Sensitivity</th>
              <th className="text-center px-4 py-2">canView</th>
              <th className="text-center px-4 py-2">canEdit</th>
              <th className="text-center px-4 py-2">canAdd</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(model?.fields ?? []).map((f) => {
              const g = getGrant(f.name);
              return (
                <tr key={f.name} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs text-gray-900">{f.name}</td>
                  <td className="px-4 py-2 text-xs text-muted">{f.type}</td>
                  <td className="px-4 py-2 text-xs">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        f.sensitivity === 'PII'
                          ? 'bg-red-50 text-red-700'
                          : f.sensitivity === 'FINANCIAL'
                            ? 'bg-amber-50 text-amber-800'
                            : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {f.sensitivity}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <button
                      type="button"
                      aria-label={`Toggle canView for ${f.name}`}
                      onClick={() => toggle(f.name, 'canView')}
                      className={`inline-flex items-center justify-center w-7 h-7 rounded-md border ${
                        g.canView
                          ? 'bg-info-light border-info text-info'
                          : 'bg-white border-border text-muted'
                      }`}
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <button
                      type="button"
                      aria-label={`Toggle canEdit for ${f.name}`}
                      onClick={() => toggle(f.name, 'canEdit')}
                      className={`inline-flex items-center justify-center w-7 h-7 rounded-md border ${
                        g.canEdit
                          ? 'bg-warning-light border-warning text-warning'
                          : 'bg-white border-border text-muted'
                      }`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <button
                      type="button"
                      aria-label={`Toggle canAdd for ${f.name}`}
                      onClick={() => toggle(f.name, 'canAdd')}
                      className={`inline-flex items-center justify-center w-7 h-7 rounded-md border ${
                        g.canAdd
                          ? 'bg-success-light border-success text-success'
                          : 'bg-white border-border text-muted'
                      }`}
                    >
                      <PlusCircle className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <details className="bg-white border border-border rounded-xl p-4">
        <summary className="text-sm font-semibold text-gray-900 cursor-pointer">
          JWT claim preview
        </summary>
        <p className="text-xs text-muted mt-2 mb-2">
          The shape that would be merged into the{' '}
          <code className="font-mono">field_permissions</code> claim for this role.
        </p>
        <pre className="text-xs font-mono bg-gray-50 rounded-lg p-3 overflow-x-auto">
{JSON.stringify(
  Object.fromEntries(
    (model?.fields ?? []).map((f) => [`${modelId}.${f.name}`, getGrant(f.name)]),
  ),
  null,
  2,
)}
        </pre>
      </details>

      <p className="text-xs text-muted">
        Roles list is mirrored client-side. To inspect a different role catalogue, see{' '}
        <Link to="/security/roles" className="font-semibold text-nipost-green hover:underline">
          Roles
        </Link>.
      </p>
    </div>
  );
}
